import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { rgba } from "polished";
import { MatIcon, FormTextbox, BlockButton, Card, Alert } from "fiver-react-ui-components";
import WalletButtonsTransactions from "./WalletButtonsTransactions";
import WalletButtonsDepositLimit from "./WalletButtonsDepositLimit";
import WalletButtonsWithdraw from "./WalletButtonsWithdraw";
import WalletButtonsDeposit from "./WalletButtonsDeposit";
import WalletButtonsDepositFonix from "./WalletButtonsDepositFonix";
import WalletButtonsFonixSvg from "./WalletButtonsFonixSvg";
const StyledRow = styled.div`
  overflow: hidden;
  ${props => !props.isActive && "max-height: 56px"};
`;
const StyledIcon = styled(MatIcon)`
  color: ${props => props.theme.colorLabel};
`;
const StyledFonixIcon = styled.span`
  display: block;
  width: 24px;
  height: 24px;
`;
const StyledText = styled.span`
  display: flex;
  align-items: center;
  margin-left: 1rem;
  font-size: 0.875rem;
`;
const StyledTextChip = styled.span`
  background: ${p => p.theme.colorSuccess};
  color: ${p => p.theme.colorSuccessText};
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  margin-left: 0.5rem;
`;
const sharedStyles = css`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-family: ${p => p.theme.fontFamilyDefault};
  color: ${p => p.theme.colorText};
  width: 100%;
  height: 56px;
  padding: 0 1rem;
`;
const StyledButton = styled.button`
  ${sharedStyles};
`;
const StyledAnchor = styled.a`
  ${sharedStyles};
`;
const StyledDisabledButton = styled.div`
  ${sharedStyles};
  color: ${p => rgba(p.theme.colorDisabledText, 0.55)};
  cursor: not-allowed;

  & > ${StyledIcon} {
    color: ${p => rgba(p.theme.colorDisabledText, 0.55)};
  }
`;
const StyledDisabledMessage = styled.div`
  padding: 0 2rem;
  font-size: 0.875rem;
  color: ${p => p.theme.colorLabel};
  margin-bottom: 1rem;
  line-height: 1.5;

  & a {
    color: inherit;
    font-size: inherit;
    text-decoration: underline;
  }
`;
const StyledContent = styled.div`
  padding: 0 2rem 1rem;
  border-bottom: 2px solid ${props => props.theme.colorBorder};
`;

const WalletButtons = ({
  walletLoading,
  addCardUrl,
  addCardError,
  isIframe,
  dateLocale,
  hideGamblingReferences,
  hideTransactions,
  hideDeposit,
  showFonixDeposit,
  translations,
  transactionsUpdated,
  setTransactionsUpdated,
  onError,
  getWallet,
  currencySymbol,
  balance,
  depositLimit,
  depositLimitToShow,
  hasPendingLimit,
  isLoading,
  errorMessage,
  setPromoCode,
  activeButton,
  toggleActive,
  transactions,
  transactionsTotal,
  loadMoreTransactions,
  isPhoneNumberVerified,
  cardAdded,
  myId
}) => {
  const buttons = [{
    name: translations.addCard,
    icon: "credit_card",
    isDisabled: !addCardUrl,
    hrefUrl: addCardUrl,
    errorMessage: addCardError
  }, {
    name: translations.withdraw,
    icon: "keyboard_return",
    onClick: name => {
      toggleActive(name);
    },
    isActive: !cardAdded || activeButton === translations.addCardToWithdraw,
    isDisabled: !cardAdded || walletLoading,
    hasError: !cardAdded || walletLoading,
    errorMessage: translations.addCardToWithdraw,
    component: /*#__PURE__*/React.createElement(WalletButtonsWithdraw, {
      setTransactionsUpdated: setTransactionsUpdated,
      currencySymbol: currencySymbol,
      translations: translations,
      onError: onError,
      getWallet: getWallet,
      balance: balance
    })
  }, {
    name: translations.deposit,
    icon: "library_add",
    onClick: name => {
      toggleActive(name);
    },
    isHidden: hideDeposit,
    isActive: !cardAdded || activeButton === translations.deposit,
    isDisabled: !cardAdded || walletLoading,
    hasError: !cardAdded || walletLoading,
    errorMessage: translations.addCardToDeposit,
    component: /*#__PURE__*/React.createElement(WalletButtonsDeposit, {
      setTransactionsUpdated: setTransactionsUpdated,
      currencySymbol: currencySymbol,
      translations: translations,
      onError: onError,
      getWallet: getWallet,
      balance: balance,
      myId: myId
    })
  }, {
    name: `${translations.deposit} Fonix`,
    iconComponent: /*#__PURE__*/React.createElement(StyledFonixIcon, null, /*#__PURE__*/React.createElement(WalletButtonsFonixSvg, null)),
    onClick: name => {
      toggleActive(name);
    },
    isHidden: !showFonixDeposit || hideDeposit,
    isActive: activeButton === `${translations.deposit} Fonix`,
    isDisabled: !isPhoneNumberVerified || walletLoading,
    hasChip: true,
    hasError: !isPhoneNumberVerified || walletLoading,
    errorMessage: "Please, verify phone number first",
    component: /*#__PURE__*/React.createElement(WalletButtonsDepositFonix, {
      currencySymbol: currencySymbol,
      onError: onError
    })
  }, {
    name: translations.transactions,
    icon: "receipt_long",
    onClick: name => {
      window.xtremepush("tag", "Wallet.Transactions", JSON.stringify({
        AppName: process.env.REACT_APP_TITLE
      }));
      toggleActive(name);
    },
    isHidden: hideTransactions,
    isActive: activeButton === translations.transactions,
    component: /*#__PURE__*/React.createElement(WalletButtonsTransactions, {
      onError: onError,
      dateLocale: dateLocale,
      translations: translations,
      currencySymbol: currencySymbol,
      transactionsUpdated: transactionsUpdated,
      setTransactionsUpdated: setTransactionsUpdated
    })
  }, {
    name: translations.depositLimit,
    icon: "app_blocking",
    onClick: name => {
      window.xtremepush("tag", "Wallet.DepositLimit", JSON.stringify({
        AppName: process.env.REACT_APP_TITLE
      }));
      toggleActive(name);
    },
    isHidden: hideGamblingReferences || hideDeposit,
    isActive: activeButton === translations.depositLimit,
    component: /*#__PURE__*/React.createElement(WalletButtonsDepositLimit, {
      onError: onError,
      translations: translations,
      errorMessage: errorMessage,
      currencySymbol: currencySymbol,
      dateLocale: dateLocale
    })
  }];
  return /*#__PURE__*/React.createElement(Card, {
    padding: "1rem 0"
  }, buttons.map(item => !item?.isHidden ? /*#__PURE__*/React.createElement(StyledRow, {
    key: item?.name,
    isActive: item?.isActive || activeButton === item?.name,
    onClick: () => item?.onClick ? item?.onClick(item?.name) : null
  }, item?.isDisabled && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledDisabledButton, null, item?.iconComponent || /*#__PURE__*/React.createElement(StyledIcon, null, item.icon), /*#__PURE__*/React.createElement(StyledText, null, item?.name, item?.hasChip && /*#__PURE__*/React.createElement(StyledTextChip, null, "New"))), item?.hasError && /*#__PURE__*/React.createElement(StyledDisabledMessage, null, /*#__PURE__*/React.createElement(Alert, {
    isDanger: true,
    textAlign: "left"
  }, item?.errorMessage))), !item?.isDisabled && /*#__PURE__*/React.createElement(React.Fragment, null, item?.onClick && /*#__PURE__*/React.createElement(StyledButton, {
    onClick: item?.onClick
  }, item?.iconComponent || /*#__PURE__*/React.createElement(StyledIcon, null, item.icon), /*#__PURE__*/React.createElement(StyledText, null, item.name, item?.hasChip && /*#__PURE__*/React.createElement(StyledTextChip, null, "New"))), item?.hrefUrl && /*#__PURE__*/React.createElement(StyledAnchor, {
    href: item?.hrefUrl,
    target: "_blank"
  }, item?.iconComponent || /*#__PURE__*/React.createElement(StyledIcon, null, item.icon), /*#__PURE__*/React.createElement(StyledText, null, item.name, item?.hasChip && /*#__PURE__*/React.createElement(StyledTextChip, null, "New"))), item?.component && /*#__PURE__*/React.createElement(StyledContent, {
    onClick: e => {
      e.stopPropagation();
    }
  }, item?.component))) : null));
};

export default WalletButtons;